import { useEntityTable } from "@mirage/store/entityTableStore";
import { Box, Button, Divider, Popover, Stack } from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { GetTimeRangeGroups, NamedTimeRanges, TimeRangeGroup } from "@shared/mirage/entities/NamedTimeRanges";
import { DateFilterPopperGroup } from "./DateFilterPopperGroup";
import { CalendarService } from "@mirage/services/CalendarService";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { EntityHelper } from "../../EntityTableHelper";
import { DateRange } from "@shared/mirage/services/ICalendarService";
import Field, { MirageOnChangeEvent } from "@mirage/components/regularField/Field";
import "./DateFilterModal.css";

const calendar = new CalendarService();
const CustomGroupedTimeRangesColumn: Array<Array<keyof typeof NamedTimeRanges>> = [
    ["Fiscal_Year", "Fiscal_Quarter"],
    ["Calendar_Year", "Calendar_Quarter"],
    ["Calendar_Month", "Calendar_Week"],
    ["Calendar_Day"],
];

const DateFilterModal = memo(() => {
    const timeRangeGroups = GetTimeRangeGroups();
    const timeRangesAsColumns: TimeRangeGroup[][] = useMemo(() => {
        const remappedColumns = CustomGroupedTimeRangesColumn.map((column) => {
            return column.map((key) => {
                const foundGroup = timeRangeGroups.find((group) => group.key === key);
                if (foundGroup) {
                    timeRangeGroups.splice(timeRangeGroups.indexOf(foundGroup), 1);
                }
                return foundGroup;
            });
        });
        timeRangeGroups.map((group) => {
            remappedColumns.push([group]);
        });
        return remappedColumns as TimeRangeGroup[][];
    }, [timeRangeGroups]);
    const { setDateFilterPopper, dateFilterPopper, apiRef, showAlert } = useEntityTable();
    const defaultTimeRange: [null, null] = useMemo(() => [null, null], []);
    const [timeRange, setTimeRange] = useState<[null | Date, null | Date]>(defaultTimeRange);
    const closeModal = useCallback(() => {
        setDateFilterPopper({ open: false, elementRef: null, fieldName: "" });
        setTimeRange(defaultTimeRange);
    }, [defaultTimeRange, setDateFilterPopper]);
    const onDateRangeChange = useCallback((e: MirageOnChangeEvent, range: [Date | null, Date | null]) => setTimeRange(range), []);
    const applyRangeOnClick = useCallback(() => {
        EntityHelper.replaceFilterForColumnField(dateFilterPopper?.fieldName || "", "between", timeRange, apiRef);
        closeModal();
    }, [dateFilterPopper?.fieldName, timeRange, apiRef, closeModal]);
    return (
        <Popover open={dateFilterPopper?.open || false} anchorEl={dateFilterPopper?.elementRef} anchorOrigin={{ vertical: "bottom", horizontal: "left" }} onClose={closeModal}>
            <Box padding={2} display="flex" flexDirection="column" gap="2rem">
                <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                    {timeRangesAsColumns.map((groups, idx) => {
                        return (
                            <Box key={idx}>
                                {groups.map((group, idx) => {
                                    return (
                                        <DateFilterPopperGroup
                                            key={idx + group.label}
                                            title={group.label}
                                            timeRanges={group.items}
                                            setDateFilterPopper={setDateFilterPopper}
                                            onClick={(range) => handleOnRangeClicked(range, dateFilterPopper?.fieldName || "", apiRef)}
                                        />
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Stack>
                <Box display="flex" gap="2rem">
                    <Box minWidth="250px" maxWidth="500px">
                        <Field label="" containerClassName="datePickerContainer" contentType="DATEBETWEEN" mode="edit" value={timeRange} onChange={onDateRangeChange} />
                    </Box>
                    <Button variant="contained" onClick={applyRangeOnClick} sx={{ height: "fit-content", alignSelf: "center" }}>
                        Apply Range
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
    async function handleOnRangeClicked(range: string, fieldName: string, apiRef: React.MutableRefObject<GridApiPremium>) {
        const resp = await calendar.resolveNamedRange(range);
        if (resp.errorMsg) {
            showAlert(resp.errorMsg, "error");
            return;
        } else {
            const range = resp.range as DateRange;
            EntityHelper.replaceFilterForColumnField(fieldName, "between", [range.start, range.end], apiRef);
            closeModal();
        }
    }
});

interface DateFilterRecord {
    label: string;
    onClick: () => void;
}

export interface DateFilters {
    fiscalYear: DateFilterRecord[];
    fiscalQuarter: DateFilterRecord[];
    calendarYear: DateFilterRecord[];
    calendarQuarter: DateFilterRecord[];
    calendarMonth: DateFilterRecord[];
    calendarWeek: DateFilterRecord[];
    day: DateFilterRecord[];
    months: (year?: number) => DateFilterRecord[];
}

export default DateFilterModal;
